import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DraftsRoundedIcon from '@mui/icons-material/DraftsRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import UsersMenu from '@features/UsersMenu';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import Avatar, { RemovedPersonAvatar } from '@components/Avatar';
import { UserIcon, PulseAiIcon } from 'assets/images/icons';
import { getFullName, getInitialsParts, getUserContacts } from '@helpers/userName';
import { getConversationChannelIcon } from '@helpers/channels';
import { handleApiError } from '@helpers/api';
import { useAccountPeople, useTranslation, useConversations } from '@hooks';
import { ConversationStatus } from '@constants/conversations';
import { CHANNEL } from '@constants/channels';
import { contactsActions, inboxActions } from '@actions';
import { conversationsService } from '@services';
import { useMoveToTeam } from '../useMoveToTeam';
import { useAssignConversation } from '../useAssignConversation';
import { useAssigneeOptions } from '../useAssigneeOptions';

const StyledUserIcon = styled(UserIcon, {
    shouldForwardProp: prop => prop !== 'isLarge',
})(({ isLarge }) => ({
    width: `${isLarge ? 18 : 16}px !important`,
    height: `${isLarge ? 18 : 16}px !important`,
    '& path': {
        fill: 'var(--text-tertiary)',
        fillOpacity: 1,
    },
}));

const StyledAvatar = styled(Avatar)({
    border: 'none',
});

const InboxHeader = ({
    isMobile,
    activeConversation,
    setActiveConversation,
    filterRef,
    setContactOpened,
    assignedUser,
    isAssignedToOtherUser,
    canReplyByTeam = true,
}) => {
    const [isSolvingConversation, setIsSolvingConversation] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    const { contact } = useSelector(state => state.contacts);
    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const isLargeScreenWithOpenedContact = useMediaQuery('@media (min-width: 1025px) and (max-width: 1100px)');
    const dispatch = useDispatch();
    const getAssigneeOptions = useAssigneeOptions();
    const { updateConversationInFeedAndReturn } = useConversations();
    const { canMoveToTeam, openMoveToTeam, moveToTeamModal } = useMoveToTeam({
        activeConversation,
        setActiveConversation,
        filterRef,
    });
    const assignConversation = useAssignConversation({ setActiveConversation, filterRef });

    const usersOptions = useMemo(
        () => getAssigneeOptions(activeConversation?.teamId),
        [activeConversation?.teamId, getAssigneeOptions],
    );

    const onClickBack = () => setActiveConversation(null);

    const onClickContactDetails = () => {
        if (activeConversation?.contactId !== contact?.id) {
            dispatch(contactsActions.getContact(activeConversation?.contactId));
        }
        setContactOpened(true);
    };

    const onResolveConversation = async () => {
        try {
            setIsSolvingConversation(true);
            const apiCall = isOpen ? conversationsService.close : conversationsService.open;
            const { message, conversation } = await apiCall(activeConversation?.id);
            setActiveConversation(prev => (prev?.id === conversation.id ? conversation : prev));
            if (message) {
                dispatch(inboxActions.addMessage(conversation.id, message));
            }
            updateConversationInFeedAndReturn(conversation, filterRef.current, true);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSolvingConversation(false);
        }
    };

    const onAssignUser = userId => {
        // The Assign button calls with no argument (assign to me); an explicit null
        // from the menu means unassign
        const targetUserId = userId === undefined ? currentUser?.id : userId;
        if ((activeConversation?.assignedUserId ?? null) === (targetUserId ?? null)) {
            return;
        }
        return assignConversation(activeConversation?.id, targetUserId);
    };

    const isOpen = activeConversation?.status === ConversationStatus.Open;
    const isArchived = activeConversation?.channelId === CHANNEL.ARCHIVED;
    const canReopen = !isArchived && !activeConversation?.isImported;

    const assigneeAvatar = activeConversation?.assignedAgentId ? (
        <StyledAvatar size="small" icon={<PulseAiIcon />} />
    ) : assignedUser?.isRemoved ? (
        <RemovedPersonAvatar size="small" sx={{ border: 'none' }} />
    ) : assignedUser ? (
        <StyledAvatar
            size="small"
            image={assignedUser.profileImageUrl}
            fullName={getFullName(assignedUser.firstName, assignedUser.lastName)}
            label={getInitialsParts(assignedUser.firstName, assignedUser.lastName)}
        />
    ) : (
        <StyledAvatar size="small" icon={<StyledUserIcon />} />
    );

    return (
        <>
            {isMobile && (
                <ArrowBackIcon sx={{ cursor: 'pointer', fill: 'var(--text-secondary)' }} onClick={onClickBack} />
            )}
            <Avatar
                size={isMobile ? 'medium' : 'large'}
                fullName={getFullName(activeConversation?.firstName, activeConversation?.lastName)}
                label={getInitialsParts(activeConversation?.firstName, activeConversation?.lastName)}
                icon={<StyledUserIcon isLarge={!isMobile} />}
                withBadge
                badge={getConversationChannelIcon(activeConversation)}
            />
            {activeConversation && (
                <>
                    <div style={{ overflow: 'hidden' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {getUserContacts(
                                {
                                    firstName: activeConversation?.firstName,
                                    lastName: activeConversation?.lastName,
                                    phoneNumber: activeConversation?.phoneNumber,
                                    email: activeConversation?.email,
                                    externalId: activeConversation?.contactExternalId,
                                },
                                '-',
                            )}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                cursor: 'pointer',
                                color: 'var(--primary-main)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                            onClick={onClickContactDetails}
                        >
                            <PersonOutlineRoundedIcon sx={{ fontSize: 'inherit' }} />
                            {t('InboxPage.viewDetails')}
                        </Typography>
                    </div>
                    {/* All right-side controls live in the header grid's third cell */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {activeConversation?.assignedAgentId || isAssignedToOtherUser ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {assigneeAvatar}
                                {canReplyByTeam && (
                                    <Button
                                        variant="outlined"
                                        disabled={!activeConversation}
                                        onClick={() => onAssignUser()}
                                    >
                                        {t(
                                            isMobile || isLargeScreenWithOpenedContact
                                                ? 'buttons.join'
                                                : 'buttons.joinConversation',
                                        )}
                                    </Button>
                                )}
                            </div>
                        ) : !canReplyByTeam ? (
                            // Cross-team collaborators read and note; assignment and resolution belong to the owning team
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {assigneeAvatar}
                                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                    {assignedUser ? getPersonName(assignedUser.id) : t('common.unassigned')}
                                </Typography>
                            </div>
                        ) : (
                            <div style={{ display: 'flex' }}>
                                <UsersMenu
                                    options={usersOptions}
                                    value={activeConversation.assignedUserId ?? null}
                                    onSelect={onAssignUser}
                                    label={assignedUser?.isRemoved ? getPersonName(assignedUser.id) : undefined}
                                />
                                <Button
                                    variant="outlined"
                                    sx={isMobile ? { minWidth: 'auto' } : undefined}
                                    disabled={!activeConversation || (!isOpen && !canReopen) || isSolvingConversation}
                                    onClick={onResolveConversation}
                                    startIcon={
                                        !isMobile ? isOpen ? <CheckRoundedIcon /> : <DraftsRoundedIcon /> : undefined
                                    }
                                >
                                    {isMobile ? (
                                        isOpen ? (
                                            <CheckRoundedIcon />
                                        ) : (
                                            <DraftsRoundedIcon />
                                        )
                                    ) : (
                                        t(isOpen ? 'buttons.resolve' : 'buttons.reopen')
                                    )}
                                </Button>
                            </div>
                        )}
                        {canMoveToTeam && (
                            <>
                                <IconButton
                                    sx={{ height: '32px', width: '32px', marginLeft: '4px' }}
                                    onClick={e => setMenuAnchorEl(e.currentTarget)}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchorEl}
                                    open={!!menuAnchorEl}
                                    onClose={() => setMenuAnchorEl(null)}
                                    sx={{ '& .MuiMenuItem-root': { fontSize: 14, lineHeight: '16px' } }}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            setMenuAnchorEl(null);
                                            openMoveToTeam();
                                        }}
                                    >
                                        {t('InboxPage.moveToTeam')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </div>
                    {moveToTeamModal}
                </>
            )}
        </>
    );
};

export default InboxHeader;
